import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../ui/Button/Button';
import IconButton from '../../ui/IconButton';
import Icon from '../../ui/Icon';
import Card from '../../ui/Card';
import { CellText, CellId, CellMoney, CellDate, CellTag } from '../../ui/DataTable/cells';
import Tabs from '../../ui/Tabs/Tabs';
import Dialog from '../../ui/Dialog/Dialog';
import DataTable from '../../ui/DataTable/DataTable';
import EmptyState from '../../ui/EmptyState';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import { ICONS } from '../../ui/icon-map';
import CustodyStatusBadge from './components/CustodyStatusBadge';
import { custodyService } from '../../services/custodyService';
import ExpenseForm from './components/ExpenseForm';
import { utils, writeFile } from 'xlsx';
import { useModal } from '../../contexts/ModalContext';
import { usePermissions } from '../../hooks/usePermissions';
import ActionMenu from '../../ui/ActionMenu';
import { custodyActionsFor, custodyErrorText, settlementRecord, reimbursementSheetRows } from './custodyRules';
import { formatMoney } from '../../constants/finance';

/** Today in Dubai as yyyy-mm-dd (toISOString gave yesterday before 04:00 Dubai time). */
const dubaiDay = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

const CustodyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showConfirm, showToast } = useModal();
    const { hasPermission } = usePermissions();
    // Buttons only for the writes the database rules allow this user (ADM-18)
    const allowed = custodyActionsFor(hasPermission);
    const [loadError, setLoadError] = useState('');
    const [custody, setCustody] = useState(null);
    const [expenses, setExpenses] = useState([]);
    // const [settlements, setSettlements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('expenses');
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [custodyData, expensesData] = await Promise.all([
                custodyService.getCustodyById(id),
                custodyService.getExpensesByCustodyId(id)
            ]);

            if (!custodyData) {
                navigate('/custodies');
                return;
            }

            setCustody(custodyData);
            setExpenses(expensesData);
            setLoadError('');
        } catch (error) {
            // a denied / failed read is shown as such, not as "Custody not found" or no expenses
            setLoadError(error?.code === 'permission-denied'
                ? 'Your role cannot read the petty cash records (database permission). Ask an admin.'
                : `This custody could not be loaded (${error?.code || error?.message || 'unknown error'}).`);
        } finally {
            setLoading(false);
        }
    };

    /** Runs one write; a refused or failed write is said, not silently followed by a refresh. */
    const runWrite = async (write, what, after) => {
        const res = await write();
        if (!res || res.success === false) {
            showToast(custodyErrorText(res, what), 'error');
            return false;
        }
        after();
        return true;
    };

    useEffect(() => {
        fetchData();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExpenseAdded = () => {
        setShowExpenseModal(false);
        setEditingExpense(null);
        fetchData();
    };

    const handleEditExpense = (expense) => {
        setEditingExpense(expense);
        setShowExpenseModal(true);
    };

    const handleDeleteExpense = (expense) => {
        showConfirm({
            title: 'Delete Expense',
            message: `Are you sure you want to delete the expense "${expense.description}"?`,
            confirmLabel: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                await runWrite(() => custodyService.deleteExpense(id, expense.id, expense.amount), 'Deleting the expense', fetchData);
            }
        });
    };

    const handleArchive = () => {
        showConfirm({
            title: 'Archive Custody',
            message: 'Archive this custody? It will be moved from the main dashboard.',
            confirmLabel: 'Archive',
            onConfirm: async () => {
                await runWrite(() => custodyService.archiveCustody(id), 'Archiving the custody', () => navigate('/custodies'));
            }
        });
    };

    const handleDeleteCustody = () => {
        showConfirm({
            title: 'Delete Custody',
            message: 'DANGER: Are you sure you want to PERMANENTLY DELETE this custody? This cannot be undone.',
            confirmLabel: 'PERMANENTLY DELETE',
            isDangerous: true,
            onConfirm: async () => {
                await runWrite(() => custodyService.deleteCustody(id), 'Deleting the custody', () => navigate('/custodies'));
            }
        });
    };

    const handleExportExcel = () => {
        const wb = utils.book_new();
        const dateStr = new Date().toLocaleDateString('en-GB');

        // the expenses, then the total row under its own Custody / Spent / Remaining header (ADM-24)
        const reportData = reimbursementSheetRows(custody, expenses, dateStr);

        const wsReport = utils.aoa_to_sheet(reportData);
        wsReport['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        wsReport['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
        utils.book_append_sheet(wb, wsReport, "Reimbursement");

        const expensesData = expenses.map(ex => ({
            Date: ex.expense_date,
            Category: ex.category,
            Description: ex.description,
            Amount: Number(ex.amount),
            'Invoice No.': ex.invoice_number,
            Payment: ex.payment_method
        }));
        const wsExpenses = utils.json_to_sheet(expensesData);
        utils.book_append_sheet(wb, wsExpenses, "Detailed Expenses");

        // Sanitize filename to prevent path traversal
        const sanitizeFilename = (name) => {
            if (!name) return 'Custody';
            return String(name)
                .replace(/[^a-zA-Z0-9_\-\s]/g, '') // Remove special chars
                .replace(/\.\.+/g, '') // Remove path traversal attempts
                .replace(/[/\\]/g, '') // Remove slashes
                .trim()
                .substring(0, 50) || 'Custody'; // Limit length
        };

        const safeFilename = sanitizeFilename(custody.name);
        writeFile(wb, `Custody_Reimbursement_${safeFilename}.xlsx`);
    };

    const handleSettlementSubmit = () => {
        showConfirm({
            title: 'Submit Settlement',
            message: `Submit the settlement: spent ${formatMoney(custody.spent)}, remaining cash ${formatMoney(custody.remaining)}?`,
            confirmLabel: 'Submit',
            onConfirm: async () => {
                // settled = spent, difference = the cash still in hand (was always stored as 0 - ADM-24)
                await runWrite(() => custodyService.submitSettlement(id, settlementRecord(custody, dubaiDay())),
                    'Submitting the settlement', fetchData);
            }
        });
    };

    const handleReopen = () => {
        showConfirm({
            title: 'Reopen Custody',
            message: 'Are you sure you want to reopen this custody?',
            confirmLabel: 'Reopen',
            onConfirm: async () => {
                await runWrite(() => custodyService.reopenCustody(id), 'Reopening the custody', fetchData);
            }
        });
    };

    if (loading) return <Layout title="Custody" backPath="/custodies"><LoadingSpinner message="Loading..." /></Layout>;
    if (loadError) {
        return (
            <Layout title="Custody" backPath="/custodies">
                <EmptyState icon={ICONS.wallet} title="Could not load this custody" description={loadError}
                    action={<Button onClick={fetchData} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Retry</Button>} />
            </Layout>
        );
    }
    if (!custody) return <Layout title="Custody" backPath="/custodies"><EmptyState icon={ICONS.wallet} title="Custody not found" /></Layout>;

    const isOpen = custody.status === 'open';
    const isSettled = custody.status === 'settled' || custody.status === 'settlement_submitted';

    const expenseColumns = [
        {
            key: 'expense_date',
            header: 'Date',
            sortValue: (ex) => ex.expense_date,
            cell: (ex) => <CellDate value={ex.expense_date} />,
        },
        { key: 'category', header: 'Category', cell: (ex) => <CellTag>{ex.category}</CellTag> },
        { key: 'description', header: 'Description', nowrap: false, cell: (ex, v) => <CellText>{v}</CellText> },
        { key: 'invoice_number', header: 'Invoice No.', cell: (ex) => <CellId>{ex.invoice_number}</CellId> },
        {
            key: 'amount',
            header: 'Amount',
            sortValue: (ex) => Number(ex.amount),
            cell: (ex) => <CellMoney value={Number(ex.amount)} currency="AED" />,
        },
        {
            key: 'attachment_url',
            header: 'Receipt',
            sortable: false,
            cell: (ex) => (ex.attachment_url ? (
                <Button variant="link" size="sm" leadingIcon={<Icon glyph={ICONS.eye} size="xs" />} onClick={() => window.open(ex.attachment_url, '_blank')}>
                    View
                </Button>
            ) : <span className="ds-cell-empty">—</span>),
        },
        ...(isOpen && allowed.expenses ? [{
            key: 'actions',
            header: 'Actions',
            sortable: false,
            searchable: false,
            cell: (ex) => (
                <div className="flex justify-center gap-1">
                    <IconButton icon={ICONS.edit} label="Edit expense" size="sm" onClick={() => handleEditExpense(ex)} />
                    <IconButton icon={ICONS.delete} label="Delete expense" size="sm" tone="danger" onClick={() => handleDeleteExpense(ex)} />
                </div>
            ),
        }] : []),
    ];

    return (
        <Layout
            title={`Custody: ${custody.name}`}
            backPath="/custodies"
            subtitle={(
                <span className="inline-flex flex-wrap items-center gap-2">
                    <CustodyStatusBadge status={custody.status} remaining={custody.remaining} />
                    <span>Opened on {new Date(custody.open_date).toLocaleDateString('en-GB')}</span>
                </span>
            )}
            actions={(
                <>
                    {hasPermission('export_custody') && <Button onClick={handleExportExcel} leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                        Export Excel
                    </Button>}
                    <ActionMenu
                        label="More"
                        icon={ICONS.more}
                        width={220}
                        items={[
                            {
                                label: 'Reopen',
                                icon: ICONS.unlock,
                                onClick: handleReopen,
                                hidden: !isSettled || !allowed.status,
                            },
                            {
                                label: 'Archive',
                                icon: ICONS.archive,
                                onClick: handleArchive,
                                hidden: !isSettled || !allowed.status,
                            },
                            { divider: true },
                            {
                                label: 'Delete',
                                icon: ICONS.delete,
                                danger: true,
                                onClick: handleDeleteCustody,
                                hidden: !allowed.remove,
                            },
                        ]}
                    />
                </>
            )}
        >
            <div className="flex flex-col gap-4">

                {/* Financial Summary */}
                <StatGrid min={200}>
                    <Stat label="Total Custody Amount" value={formatMoney(Number(custody.total_amount) || 0)} icon={ICONS.wallet} />
                    <Stat label="Total Spent" value={formatMoney(Number(custody.spent) || 0)} icon={ICONS.operations} tone="danger" />
                    <Stat
                        label="Remaining Balance"
                        value={formatMoney(Number(custody.remaining) || 0)}
                        icon={ICONS.history}
                        tone={Number(custody.remaining) < 0 ? 'danger' : 'success'}
                    />
                </StatGrid>

                {/* Tabs */}
                <Tabs
                    items={[
                        { id: 'expenses', label: 'Expenses', count: expenses.length },
                        { id: 'settlements', label: 'Settlements' },
                    ]}
                    value={activeTab}
                    onChange={setActiveTab}
                    label="Custody sections"
                    idPrefix="custody"
                />

                {/* Content */}
                {activeTab === 'expenses' && (
                    <div className="flex flex-col gap-4" role="tabpanel" id="custody-panel-expenses" aria-labelledby="custody-expenses">
                        <DataTable
                            columns={expenseColumns}
                            data={expenses}
                            rowKey="id"
                            title="Expenses"
                            searchPlaceholder="Search expenses…"
                            headerActions={isOpen && allowed.expenses ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => { setEditingExpense(null); setShowExpenseModal(true); }}
                                    leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}
                                >
                                    Add Expense
                                </Button>
                            ) : null}
                            emptyTitle="No expenses recorded yet."
                            emptyIcon={ICONS.invoices}
                        />

                        {/* Settlement Action */}
                        {isOpen && allowed.settle && (
                            <div className="flex justify-end pt-4 border-t border-gray-200">
                                <Button variant="primary" onClick={handleSettlementSubmit}>
                                    Submit Settlement for Review
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'settlements' && (
                    <Card role="tabpanel" id="custody-panel-settlements" aria-labelledby="custody-settlements">
                        <EmptyState icon={ICONS.history} title="No settlement history available." />
                    </Card>
                )}

                {/* Expense Modal */}
                <Dialog
                    open={showExpenseModal}
                    onClose={() => setShowExpenseModal(false)}
                    title={editingExpense ? 'Edit Expense' : 'Add New Expense'}
                    closeOnOverlay={false}
                >
                    <ExpenseForm
                        custodyId={id}
                        initialData={editingExpense}
                        onSuccess={handleExpenseAdded}
                        onCancel={() => setShowExpenseModal(false)}
                    />
                </Dialog>

            </div>
        </Layout>
    );
};

export default CustodyDetails;
